import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { translateUi } from '../../i18n/uiDictionary';

/**
 * <T> Translate Component
 *
 * Wraps English text and renders it in the current UI language (EN/DE/IT/FR/ES)
 * using the static offline dictionary in i18n/uiDictionary.js. Strings not in
 * the dictionary fall back to the original English, so partial coverage is safe.
 *
 * Usage:
 *   <T>Repair Process Details</T>
 *   <T>{"Step " + n}</T>   // only literal English keys are translated
 *
 * For attributes (placeholder, title, label, aria-label) use useAutoTranslate().
 */
function T(props) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const isText = typeof props.children === 'string' || typeof props.children === 'number';

  // Non-text children (nested JSX) render unchanged.
  if (!isText) {
    return props.children || null;
  }

  return translateUi(String(props.children), lang);
}

/**
 * useAutoTranslate hook
 *
 * For translating strings used in attributes/variables where a component can't
 * be rendered. Returns a function at(text) => translated string.
 *
 * Usage:
 *   const at = useAutoTranslate();
 *   <input placeholder={at('Search by vehicle...')} />
 *   at('Select tour {{tour}}', { tour: trackNumber })
 *
 * Translate fixed interface text only. Inserted values, customer names,
 * messages, and persisted workflow codes must retain their original content.
 */
export function useAutoTranslate() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  // Memoize on `lang` so the returned function keeps a stable identity across
  // renders. Consumers commonly put `at` in useEffect/useCallback dependency
  // arrays; a fresh function each render would retrigger those effects every
  // render — an infinite re-render/refetch loop (e.g. StatusHistoryCard's
  // getCallsByPhone hammering /call-history hundreds of times per second).
  return useCallback((text, values) => translateUi(text, lang, values), [lang]);
}

export function useUiLocale() {
  const { i18n } = useTranslation();
  const language = (i18n.language || 'en').slice(0, 2);
  return { de: 'de-CH', en: 'en-GB', it: 'it-CH', fr: 'fr-CH', es: 'es-ES' }[language] || 'en-GB';
}

export default T;
